import manifest from "../data/gallery.json";

/**
 * Gallery access. Every filename on the site comes from src/data/gallery.json,
 * which scripts/build-gallery-images.mjs writes — never hardcode a path into a
 * component, or adding the next batch of photographs stops being a drop-in.
 */
const P = process.env.PUBLIC_URL || "";

export const WIDTHS = manifest.widths || [400, 1200, 2000];
export const EDITIONS = manifest.editions || [];

export function edition(id) {
  return EDITIONS.find((e) => e.id === id);
}

export function allPhotos() {
  return EDITIONS.flatMap((e) => e.photos.map((p) => ({ ...p, edition: e })));
}

/** Photos flagged `featured` in the manifest, newest edition first. */
export function featured(limit) {
  const list = allPhotos().filter((p) => p.featured);
  return typeof limit === "number" ? list.slice(0, limit) : list;
}

export function byFile(file) {
  return allPhotos().find((p) => p.file === file);
}

function base(photo) {
  return `${P}/img/gallery/${photo.edition ? photo.edition.id : editionIdOf(photo)}/${photo.file}`;
}

function editionIdOf(photo) {
  const e = EDITIONS.find((ed) => ed.photos.some((p) => p.file === photo.file));
  return e ? e.id : "";
}

/** Widths actually generated for this photo — the script never upscales. */
function availableWidths(photo) {
  return WIDTHS.filter((w) => w <= photo.width);
}

export function src(photo, width) {
  const avail = availableWidths(photo);
  const w = avail.includes(width) ? width : avail[avail.length - 1];
  return `${base(photo)}-${w}.jpg`;
}

export function srcSet(photo, ext = "jpg") {
  return availableWidths(photo)
    .map((w) => `${base(photo)}-${w}.${ext} ${w}w`)
    .join(", ");
}

/**
 * Props every gallery <img> needs: explicit intrinsic dimensions (so nothing
 * reflows), a srcset, and the LQIP as a background so there is no flash.
 */
export function imgProps(photo, { sizes = "100vw", eager = false } = {}) {
  return {
    src: src(photo, 1200),
    srcSet: srcSet(photo, "jpg"),
    sizes,
    width: photo.width,
    height: photo.height,
    loading: eager ? "eager" : "lazy",
    decoding: eager ? "sync" : "async",
    ...(eager ? { fetchpriority: "high" } : {}),
  };
}

export function webpSrcSet(photo) {
  return srcSet(photo, "webp");
}
